import { Diagnostic, KnownId } from "@fairfleet/geotab";
import { LiveDataRow } from "@/pages/live-data/models/LiveDataRow";
import {
  CHECK_RULE_IDS,
  GEOTAB_IDS,
  isCanRow,
} from "@/pages/live-data/utilities/diagnosticMatchers";

/**
 * The add-on devices in use with Fairfleet's customers today: IOX-UREADER, IOX-CLAMP, IOX-COLD
 * and IOX-NFCREADER (as of 2026-10-27). Geotab's catalog has well over a hundred IOX entries,
 * nearly all for hardware no customer has; read by name they were most of the scan.
 */
const IOX_DEVICE_IDS = [
  // IOX-UREADER
  "azRjqdVaIxkSmbO5V-0khgA",
  "aLomz5it8GU-Z2Gwvd4kNRg",
  // IOX-CLAMP
  "aejCLivbm-UiGR77b1Mle1w",
  "aQ07VQrWVv0-e3bcVCQiHFA",
  // IOX-COLD
  KnownId.DiagnosticIOXColdConnectionStatusId,
  KnownId.DiagnosticIOXColdRuggedConnectionStatusId,
  KnownId.DiagnosticExternalDeviceIOXColdChainId,
  // IOX-NFCREADER
  "aZHFc8CAKMkO5uDxc-p-3xg",
  "a5ey8MdHvREaAO98WMdbjfw",
];

/**
 * The kinds of catalog entry the checklist's values are found among: Geotab's own diagnostics and
 * those a database defines itself (harness, add-on devices, customer values).
 *
 * Read by kind rather than searched by name. A name search with wildcards runs over the whole
 * catalog of more than a million entries, mostly fault codes, and fragments like "can" or "speed"
 * matched thousands of them: the searches took longer than a minute.
 */
export const CHECK_DIAGNOSTIC_TYPES = ["GoDiagnostic", "DataDiagnostic"] as const;

/**
 * The diagnostics the check reads by id: one or two per column, all of them Geotab's own.
 *
 * By id rather than by name, because the name rules matched far too much: 373 diagnostics in
 * aseag, half of them unrelated ("Bluetooth-Bake in Reichweite" as range, a tachograph download
 * box as AdBlue), each one a fleet-wide series to read. Kept in step with the rules' own ids, so
 * the expanded checklist recognises every reading the scan brings back.
 */
export const CHECK_DIAGNOSTIC_IDS: string[] = [
  ...new Set([
    ...CHECK_RULE_IDS,
    KnownId.DiagnosticRawOdometerId,
    KnownId.DiagnosticOdometerId,
    KnownId.DiagnosticFuelLevelId,
    KnownId.DiagnosticFuelUnitsId,
    GEOTAB_IDS.tachoCanC,
    GEOTAB_IDS.tachoD8,
    GEOTAB_IDS.tachoRemoteDownload,
    ...IOX_DEVICE_IDS,
  ]),
];

const FIXED_IDS = new Set(CHECK_DIAGNOSTIC_IDS);

/** "J1939/CAN-250k-Motorprotokoll erkannt", "OBD\CAN 11bit 500k engine protocol detected". */
const PROTOCOL_DETECTED = /protokoll\w*\s+erkannt|protocol\s+detected/i;

/**
 * Whether a catalog entry is one the checklist asks about.
 *
 * The fixed ids, and by name only the CAN protocol, which Geotab keeps as one diagnostic per
 * protocol and speed. Its entry has to say it detected a protocol, which leaves out the fault
 * messages that merely mention the bus ("CAN-Bus keine Kommunikation").
 *
 * Fault codes are left out, should one be passed: they are reported as FaultData rather than
 * StatusData.
 */
export function isCheckDiagnostic(
  diagnostic: Pick<Diagnostic, "id" | "name" | "diagnosticType">,
): boolean {
  if (/fault/i.test(diagnostic.diagnosticType ?? "")) {
    return false;
  }
  if (FIXED_IDS.has(diagnostic.id)) {
    return true;
  }

  const row: LiveDataRow = {
    id: diagnostic.id,
    diagnosticId: diagnostic.id,
    diagnosticName: diagnostic.name ?? "",
    value: 0,
    unitOfMeasure: "",
    dateTime: new Date(0),
    source: "",
  };

  return isCanRow(row) && PROTOCOL_DETECTED.test(row.diagnosticName);
}
